import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Grid, Input, Message, Segment } from "semantic-ui-react";
import { toast } from "../..";
import {
    editAdministratorParametersAsync,
    getAdministratorParameters,
} from "../../api/administratorSettings";
import {
    IAdministratorParameters,
    initIMinistryOfFinance,
} from "../../models/administratorSettings";

export const AdminParameters = () => {
    const intl = useIntl();
    const [administratorSettings, setAdministratorSettings] =
        useState<IAdministratorParameters>(initIMinistryOfFinance);
    const [errors, setErrors] = useState<string[]>([]);

    const handleEditParameters = async () => {
        try {
            await editAdministratorParametersAsync(administratorSettings);
            toast(intl.formatMessage({ id: "adminSettingsEdited" }), true);
        } catch {
            toast(intl.formatMessage({ id: "unableToEditAdminSettings" }), false);
        } finally {
            fetchParameters();
        }
    };

    const fetchParameters = useCallback(async () => {
        try {
            const res = await getAdministratorParameters();
            setAdministratorSettings(res);
        } catch {
            toast(intl.formatMessage({ id: "unableToFetchDataSets" }), false);
        }
    }, [intl]);

    useEffect(() => {
        fetchParameters();
    }, [fetchParameters]);

    useEffect(() => {
        if ((administratorSettings.fullAddress || []).length > 80)
            setErrors(x =>
                !x.some(e => e === "fullAddressLenght") ? x.concat("fullAddressLenght") : x
            );
        else setErrors(x => x.filter(e => e !== "fullAddressLenght"));
        if ((administratorSettings.nameLineOne || []).length > 40)
            setErrors(x =>
                !x.some(e => e === "nameLineOneLenght") ? x.concat("nameLineOneLenght") : x
            );
        else setErrors(x => x.filter(e => e !== "nameLineOneLenght"));
        if ((administratorSettings.nameLineTwo || []).length > 40)
            setErrors(x =>
                !x.some(e => e === "nameLineTwoLenght") ? x.concat("nameLineTwoLenght") : x
            );
        else setErrors(x => x.filter(e => e !== "nameLineTwoLenght"));
        if ((administratorSettings.street || []).length > 40)
            setErrors(x => (!x.some(e => e === "streetLenght") ? x.concat("streetLenght") : x));
        else setErrors(x => x.filter(e => e !== "streetLenght"));
        if ((administratorSettings.city || []).length > 40)
            setErrors(x => (!x.some(e => e === "cityLenght") ? x.concat("cityLenght") : x));
        else setErrors(x => x.filter(e => e !== "cityLenght"));
        if ((administratorSettings.zipCode || []).length > 40)
            setErrors(x => (!x.some(e => e === "zipCodeLenght") ? x.concat("zipCodeLenght") : x));
        else setErrors(x => x.filter(e => e !== "zipCodeLenght"));
        if ((administratorSettings.telephone || []).length > 40)
            setErrors(x =>
                !x.some(e => e === "telephoneLenght") ? x.concat("telephoneLenght") : x
            );
        else setErrors(x => x.filter(e => e !== "telephoneLenght"));
        if ((administratorSettings.email || []).length > 40)
            setErrors(x => (!x.some(e => e === "emailLenght") ? x.concat("emailLenght") : x));
        else setErrors(x => x.filter(e => e !== "emailLenght"));
    }, [administratorSettings]);

    return (
        <>
            <Segment className="grey-segment ">
                <Grid columns={2} stackable>
                    <Grid.Column>
                        <h2>{intl.formatMessage({ id: "ministryOfFinance" })}</h2>
                        <form className="add-user-form">
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "applicationFee" })}</label>
                                <Input
                                    value={administratorSettings.applicationFee}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            applicationFee: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "certificateFee" })}</label>
                                <Input
                                    value={administratorSettings.certificateFee}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            certificateFee: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "supplementFee" })}</label>
                                <Input
                                    value={administratorSettings.supplementFee}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            supplementFee: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>
                                    {intl.formatMessage({ id: "federalAdministrativeLevy" })}
                                </label>
                                <Input
                                    value={administratorSettings.federalAdministrativeLevy}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            federalAdministrativeLevy: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>
                                    {intl.formatMessage({ id: "excludingPaymentAmount" })}
                                </label>
                                <Input
                                    value={administratorSettings.excludingPaymentAmount}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            excludingPaymentAmount: e.target.value,
                                        })
                                    }
                                />
                            </section>
                        </form>
                        <form>
                            <h2>{intl.formatMessage({ id: "issuingAuthorityData" })}</h2>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "issuingAuthority" })}</label>
                                <Input
                                    value={administratorSettings.issuingAuthority}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            issuingAuthority: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>
                                    {intl.formatMessage({ id: "identifyingLetterOfTheAuthority" })}
                                </label>
                                <Input
                                    value={administratorSettings.identifyingLetterOfTheAuthority}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            identifyingLetterOfTheAuthority: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "authorizedBy" })}</label>
                                <Input
                                    value={administratorSettings.authorizedBy}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            authorizedBy: e.target.value,
                                        })
                                    }
                                />
                            </section>
                        </form>
                    </Grid.Column>
                    <Grid.Column className="second-form">
                        <h2>{intl.formatMessage({ id: "authority" })}</h2>
                        <form className="add-user-form">
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "nameLine1" })}</label>
                                <Input
                                    value={administratorSettings.nameLineOne}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            nameLineOne: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "nameLine2" })}</label>
                                <Input
                                    value={administratorSettings.nameLineTwo}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            nameLineTwo: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "street" })}</label>
                                <Input
                                    value={administratorSettings.street}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            street: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "city" })}</label>
                                <Input
                                    value={administratorSettings.city}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            city: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "zipCode" })}</label>
                                <Input
                                    value={administratorSettings.zipCode}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            zipCode: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "fullAddress" })}</label>
                                <Input
                                    value={administratorSettings.fullAddress}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            fullAddress: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "telephone" })}</label>
                                <Input
                                    value={administratorSettings.telephone}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            telephone: e.target.value,
                                        })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "email" })}</label>
                                <Input
                                    value={administratorSettings.email}
                                    onChange={(e: any) =>
                                        setAdministratorSettings({
                                            ...administratorSettings,
                                            email: e.target.value,
                                        })
                                    }
                                />
                            </section>
                        </form>
                    </Grid.Column>
                </Grid>
            </Segment>
            {errors.length > 0 && (
                <Message
                    className="error-message"
                    icon="exclamation circle"
                    content={errors.map(e => (
                        <span key={e}>{intl.formatMessage({ id: e })}</span>
                    ))}
                />
            )}
            <Button
                content={intl.formatMessage({ id: "ministryOfFinanceEdit" })}
                primary
                fluid
                disabled={errors.length > 0}
                onClick={handleEditParameters}
            />
        </>
    );
};
